import React, { useState } from 'react';
import type { HTMLAttributes } from 'react';
import {
	Tag,
	Dropdown,
	Checkbox,
	List,
	Tooltip,
	Button,
	Typography,
} from 'antd';
import type { SortOrder } from 'antd/es/table/interface';
import {
	DeleteOutlined,
	CheckCircleFilled,
	WarningFilled,
	EditOutlined,
	HistoryOutlined,
	MoreOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { barReservationsEnabled } from '../MaterialReservationModal/maxCutQuantity';
import { getTagColor } from '../../utils';
import { useLocationLabel } from '../../utils/locationLabel';
import CustomResponsiveTable from '../CustomResponsiveTable';
import MaterialHistoryDrawer from '../MaterialMovements/MaterialHistoryDrawer';
import {
	getColumnDimensionsFilter,
	getColumnMultiSelectFilter,
	getColumnRangeFilter,
} from './MaterialLengthsTableFilters';
import {
	ANGLE,
	CHANNEL,
	EXTRUSION,
	HEX_BAR,
	PLATE,
	RECTANGLE_BAR,
	RECTANGLE_TUBE,
	ROUND_BAR,
	ROUND_TUBE,
} from '../../config/constants';
import {
	MaterialShapePreview,
	getShapeName,
} from '../Dimensions/MaterialShapePreview';
const { Text } = Typography;

interface MaterialLengthsTableProps {
	data: any[];
	loading: boolean;
	pagination?: {
		current: number;
		pageSize: number;
		total: number;
		onChange: (page: number) => void;
	};
	onReserve?: (record: any) => void;
	onMove?: (record: any) => void;
	onDelete?: (record: any) => void;
	onSplit?: (record: any) => void;
	queryParams: URLSearchParams;
	navigate: any;
	locations: any[];
	shapes: any[];
	categories: any[];
	types: any[];
	buildings?: any[];
	dimensionRanges?: any;

	/** Tablet mode: hides Actions column, shows Select checkbox column */
	isTablet?: boolean;
	/** Currently selected row IDs (tablet mode) */
	selectedRowKeys?: React.Key[];
	/** Called when selection changes (tablet mode) */
	onSelectionChange?: (selectedKeys: React.Key[], selectedRows: any[]) => void;
	/** Override the vertical scroll height (default: calc(100vh - 310px)) */
	scrollY?: string;
	/** Building ID of the currently selected rows; rows from other buildings are disabled */
	selectedBuildingId?: string | null;
	/** Called when editing an existing reservation */
	onEditReservation?: (materialLength: any, reservation: any) => void;
	/** Called when releasing (deleting) a reservation */
	onReleaseReservation?: (reservationId: string) => void;
}

/**
 * Helper: reads a query param as string, returns empty string if absent.
 */
const param = (qp: URLSearchParams, key: string): string => qp.get(key) || '';

const formatDimension = (value: unknown) => {
	const numericValue = Number(value);
	return Number.isFinite(numericValue) && numericValue > 0
		? numericValue.toFixed(3)
		: null;
};

const getApplicableDimensions = (record: any) => {
	const shapeName = getShapeName(record);
	const dimensions: Array<{ label: string; value: string }> = [];

	const addDimension = (label: string, value: unknown) => {
		const formattedValue = formatDimension(value);
		if (formattedValue) dimensions.push({ label, value: formattedValue });
	};

	if (
		[
			RECTANGLE_BAR,
			RECTANGLE_TUBE,
			PLATE,
			ANGLE,
			CHANNEL,
			HEX_BAR,
			EXTRUSION,
		].includes(shapeName)
	) {
		addDimension('H', record.height);
	}

	if (
		[
			RECTANGLE_BAR,
			RECTANGLE_TUBE,
			PLATE,
			ANGLE,
			CHANNEL,
			EXTRUSION,
		].includes(shapeName)
	) {
		addDimension('W', record.width);
	}

	if ([ROUND_BAR, ROUND_TUBE].includes(shapeName)) {
		addDimension('OD', record.outer_diameter);
	}

	if (shapeName === ROUND_TUBE) {
		addDimension('ID', record.inner_diameter);
	}

	if ([RECTANGLE_TUBE, ROUND_TUBE, ANGLE, CHANNEL].includes(shapeName)) {
		addDimension('Wall', record.wall_thickness);
	}

	return dimensions;
};

const MaterialPreview = ({ record }: { record: any }) => (
	<div
		style={{
			width: '100%',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			textAlign: 'center',
		}}
	>
		<svg
			width={82}
			height={38}
			viewBox="0 0 100 44"
			role="img"
			aria-label={`${getShapeName(record) || 'Material'} preview`}
		>
			<MaterialShapePreview record={record} />
		</svg>
		<div style={{ marginTop: 2, display: 'flex', justifyContent: 'center' }}>
			{record.status ? (
				<Tag color={getTagColor(record.status)} style={{ marginInlineEnd: 0 }}>
					{record.status}
				</Tag>
			) : (
				'-'
			)}
		</div>
	</div>
);

const getRowBuildingId = (record: any): string | null =>
	record?.inventory_location?.shelf?.rack?.building?.id ?? null;

const getLocationDiscriminator = (record: any): string =>
	record.inventory_location?.code ||
	record.inventory_location?.display_name ||
	record.inventory_location?.id ||
	'';

const getInventoryLocationId = (record: any): string =>
	record.inventory_location?.id || '';

const MaterialLengthsTable: React.FC<MaterialLengthsTableProps> = ({
	data,
	loading,
	pagination,
	onReserve,
	onDelete,
	onSplit,
	queryParams,
	navigate,
	locations,
	shapes,
	categories,
	types,
	buildings = [],
	dimensionRanges,
	isTablet = false,
	selectedRowKeys = [],
	onSelectionChange,
	scrollY,
	selectedBuildingId,
	onEditReservation,
	onReleaseReservation,
}) => {
	const [historyRecord, setHistoryRecord] = useState<any | null>(null);
	const formatLocation = useLocationLabel();

	const getSortOrder = (columnKey: string): SortOrder => {
		const currentOrdering = queryParams.get('ordering');
		if (!currentOrdering) return null;
		if (currentOrdering === columnKey) return 'ascend';
		if (currentOrdering === `-${columnKey}`) return 'descend';
		return null;
	};

	const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
		void _filters;
		const params = new URLSearchParams(queryParams.toString());

		params.set('page', pagination.current.toString());

		if (sorter.field && sorter.order) {
			const field = sorter.columnKey || sorter.field;
			const value = sorter.order === 'descend' ? `-${field}` : field;
			params.set('ordering', value);
		} else {
			params.delete('ordering');
		}

		navigate({ search: params.toString() });
	};

	const columns = [
		{
			title: 'Length',
			width: 100,
			key: 'friendly_id',
			sorter: true,
			sortOrder: getSortOrder('friendly_id'),
			render: (_: any, record: any) => (
				<div style={{ lineHeight: 1.35 }}>
					<div>
						<Text strong style={{ fontSize: 16 }}>
							{record.friendly_id || '-'}
						</Text>
						{isTablet && (
							<Button
								type="text"
								size="small"
								aria-label="Material History"
								title="Material History"
								data-testid="material-length-history-button"
								icon={<HistoryOutlined />}
								onClick={(event) => {
									event.stopPropagation();
									setHistoryRecord(record);
								}}
							/>
						)}
					</div>
					<div>
						<Text strong style={{ fontSize: 13 }}>
							<span
								data-testid="fastlane-material-length-location"
								data-material-length-id={record.id}
								data-material-length-location={getLocationDiscriminator(record)}
								data-inventory-location-id={getInventoryLocationId(record)}
							>
								{formatLocation(record.inventory_location?.display_name) || 'No Location Assigned'}
							</span>
						</Text>
					</div>
					{record.purchase_order_number && (
						<div>
							<Text type="secondary" style={{ fontSize: 11 }}>
								PO: {record.purchase_order_number}
							</Text>
						</div>
					)}
				</div>
			),
		},
		{
			title: <div style={{ textAlign: 'center' }}>Preview</div>,
			width: 95,
			key: 'material_preview',
			align: 'center' as const,
			render: (_: any, record: any) => <MaterialPreview record={record} />,
		},
		{
			title: 'Building',
			width: 130,
			key: 'building',
			visible: !isTablet,
			dataIndex: ['inventory_location', 'shelf', 'rack', 'building', 'name'],
			sorter: true,
			sortOrder: getSortOrder('inventory_location__shelf__rack__building__name'),
			...getColumnMultiSelectFilter({
				queryParams,
				navigate,
				paramKey: 'building',
				currentValue: param(queryParams, 'building'),
				options: buildings.map((b: any) => ({
					text: b.name,
					value: b.id,
				})),
			}),
			render: (value: string) =>
				value || <Text type="secondary">—</Text>,
		},
		{
			title: 'Material',
			dataIndex: ['material_type', 'display_name'],
			key: 'material_type__display_name',
			width: 180,
			sorter: true,
			sortOrder: getSortOrder('material_type__display_name'),
			render: (value: string, record: any) => (
				<span>
					<small
						style={{ display: 'block', color: '#8c8c8c', fontSize: '10px' }}
					>
						{record.material_shape?.display_name || '-'}
					</small>
					<small
						style={{ display: 'block', color: '#8c8c8c', fontSize: '10px' }}
					>
						{record.material_type?.material_category?.display_name || '-'}
					</small>
					{value || '-'}
				</span>
			),
		},
		{
			title: 'Dimensions',
			width: 145,
			key: 'dimensions',
			...getColumnDimensionsFilter({
				queryParams,
				navigate,
				dimensionRanges,
			}),
			render: (_: any, record: any) => {
				const dimensions = getApplicableDimensions(record);

				if (dimensions.length === 0) {
					return <Text type="secondary">No dimensions</Text>;
				}

				return (
					<div style={{ lineHeight: 1.35 }}>
						{dimensions.map((dimension) => (
							<div key={dimension.label}>
								<Text type="secondary" style={{ fontSize: 11 }}>
									{dimension.label}:
								</Text>{' '}
								<Text style={{ fontSize: 12 }}>{dimension.value}</Text>
							</div>
						))}
					</div>
				);
			},
		},
		{
			title: 'Current',
			dataIndex: 'current_length',
			width: 100,
			key: 'current_length',
			sorter: true,
			sortOrder: getSortOrder('current_length'),
			...getColumnRangeFilter({
				queryParams,
				navigate,
				minParamKey: 'current_length_min',
				maxParamKey: 'current_length_max',
				currentMinValue: param(queryParams, 'current_length_min'),
				currentMaxValue: param(queryParams, 'current_length_max'),
				min: dimensionRanges?.current_length?.min || 0,
				max: dimensionRanges?.current_length?.max || 500,
			}),
			render: (value: number) => `${Number(value).toFixed(1)} in.`,
		},
		{
			title: 'Available',
			dataIndex: 'total_available_length',
			width: 110,
			key: 'total_available_length',
			sorter: true,
			sortOrder: getSortOrder('total_available_length'),
			...getColumnRangeFilter({
				queryParams,
				navigate,
				minParamKey: 'min_total_available_length',
				maxParamKey: 'max_total_available_length',
				currentMinValue: param(queryParams, 'min_total_available_length'),
				currentMaxValue: param(queryParams, 'max_total_available_length'),
				min: 0,
				max: dimensionRanges?.current_length?.max || 1000,
			}),
			render: (value: number) => (
				<Tag color={value > 0 ? 'success' : 'error'}>
					{Number(value).toFixed(1)} in.
				</Tag>
			),
		},
		{
			title: 'Cert',
			width: 90,
			dataIndex: 'has_certificate',
			key: 'has_certificate_db',
			sorter: true,
			sortOrder: getSortOrder('has_certificate_db'),
			render: (hasCertificate: boolean) => (
				<Tag
					color={hasCertificate ? 'green' : 'red'}
					style={{ fontSize: '12px' }}
				>
					{hasCertificate ? (
						<>
							<CheckCircleFilled /> Cert
						</>
					) : (
						<>
							<WarningFilled /> No Cert
						</>
					)}
				</Tag>
			),
		},
		// ── Reservations column ──────────────────────────────────────────────
		{
			title: 'Reservations',
			dataIndex: 'material_reservations',
			key: 'reservations',
			width: 160,
			render: (reservations: any[], record: any) => {
				if (!reservations || reservations.length === 0) {
					return (
						<span style={{ color: '#999', fontSize: 12 }}>No reservations</span>
					);
				}

				const activeReservations = reservations.filter(
					(r: any) => r.material_cut_status !== 'Cut'
				);

				if (activeReservations.length === 0) {
					return (
						<span style={{ color: '#999', fontSize: 12 }}>
							No active reservations
						</span>
					);
				}

				return (
					<List
						size="small"
						dataSource={activeReservations}
						split={false}
						renderItem={(reservation: any) => (
							<List.Item
								key={reservation.id}
								style={{ padding: '2px 0', border: 'none' }}
								extra={
									onEditReservation || onReleaseReservation ? (
										<Dropdown
											menu={{
												items: [
													...(onEditReservation
														? [
															{
																key: 'edit',
																label: 'Edit',
																onClick: () =>
																	onEditReservation(record, reservation),
																disabled:
																	reservation.material_cut_status !==
																	'Not Cut',
															},
														]
														: []),
													...(onReleaseReservation
														? [
															{
																key: 'release',
																label: (
																	<Tooltip title="Release this reservation back into inventory">
																		Release
																	</Tooltip>
																),
																onClick: () =>
																	onReleaseReservation(reservation.id),
																disabled:
																	reservation.material_cut_status !==
																	'Not Cut',
															},
														]
														: []),
												],
											}}
											trigger={['click']}
											placement="bottomRight"
										>
											<Button
												type="text"
												size="small"
												icon={<MoreOutlined />}
											/>
										</Dropdown>
									) : null
								}
							>
								<Tooltip
									title={`Qty: ${reservation.material_cut_quantity} | Cut: ${reservation.material_cut_length}`}
								>
									<div style={{ fontSize: 12, lineHeight: 1.4 }}>
										<span style={{ fontWeight: 500 }}>
											{reservation.reserved_length} in.
										</span>
										{' → '}
										{reservation?.order_line_item ? (
											<Link
												to={`/orders/line-items/${reservation.order_line_item.id}`}
												style={{ fontSize: 12 }}
											>
												{reservation.order_line_item.name}
												{reservation.order_line_item.order?.name &&
													` (${reservation.order_line_item.order.name})`}
											</Link>
										) : (
											<span style={{ color: '#595959' }}>
												{reservation.reserved_for_description || 'N/A'}
											</span>
										)}
										<Tag
											color={
												reservation.material_cut_status === 'Cut'
													? 'green'
													: reservation.material_cut_status === 'Not Cut'
														? 'blue'
														: 'orange'
											}
											style={{ fontSize: 10, marginLeft: 4, padding: '0 4px' }}
										>
											{reservation.material_cut_status}
										</Tag>
									</div>
								</Tooltip>
							</List.Item>
						)}
					/>
				);
			},
		},
		// ── Actions column: desktop only ─────────────────────────────────────
		...(!isTablet
			? [
				{
					title: 'Actions',
					key: 'actions',
					width: 120,
					fixed: 'right' as const,
					render: (record: any) => {
						const hasReservations =
							(record.material_reservations || []).length > 0;
						const noLength = record.total_available_length <= 0;
						const reservationsOff = !barReservationsEnabled(
							record.inventory_location
						);

						return (
							<Dropdown.Button
								menu={{
									items: [
										{
											key: 'split',
											icon: <EditOutlined />,
											label: 'Split Length',
											onClick: () => onSplit?.(record),
										},
										{
											key: 'history',
											icon: <HistoryOutlined />,
											label: 'Material History',
											onClick: () => setHistoryRecord(record),
										},
										{
											key: 'delete',
											icon: <DeleteOutlined />,
											label: 'Delete',
											onClick: () => onDelete?.(record),
											danger: true,
											disabled: hasReservations,
											title: hasReservations
												? 'Cannot delete a length with active reservations'
												: undefined,
										},
									],
								}}
								buttonsRender={([leftButton, rightButton]) => [
									<Tooltip
										key="reserve"
										title={
											reservationsOff
												? 'Reservations off for this rack/shelf'
												: undefined
										}
									>
										<span>
											{React.cloneElement(
												leftButton as React.ReactElement,
												{ disabled: noLength || reservationsOff }
											)}
										</span>
									</Tooltip>,
									rightButton,
								]}
								onClick={() => onReserve?.(record)}
								disabled={noLength}
							>
								Reserve
							</Dropdown.Button>
						);
					},
				},
			]
			: []),
		// ── Select column: always shown when onSelectionChange is provided ───
		...(onSelectionChange
			? [
				{
					title: (() => {
						const eligible = (data || []).filter(
							(r: any) =>
								!selectedBuildingId ||
								getRowBuildingId(r) === selectedBuildingId
						);
						return (
							<Checkbox
								checked={
									eligible.length > 0 &&
									eligible.every((r: any) =>
										selectedRowKeys.includes(r.id)
									)
								}
								indeterminate={
									eligible.some((r: any) =>
										selectedRowKeys.includes(r.id)
									) &&
									!eligible.every((r: any) =>
										selectedRowKeys.includes(r.id)
									)
								}
								onChange={(e) => {
									if (e.target.checked) {
										const eligibleKeys = eligible.map(
											(r: any) => r.id
										);
										const merged = [
											...new Set([
												...selectedRowKeys,
												...eligibleKeys,
											]),
										];
										onSelectionChange(merged, []);
									} else {
										const currentPageKeys = new Set(
											(data || []).map((r: any) => r.id)
										);
										const remaining = selectedRowKeys.filter(
											(k) => !currentPageKeys.has(k)
										);
										onSelectionChange(remaining, []);
									}
								}}
							/>
						);
					})(),
					key: 'select',
					width: 60,
					maxWidth: 60,
					fixed: 'right' as const,
					className: 'select-column',
					render: (_: any, record: any) => {
						const rowBuilding = getRowBuildingId(record);
						const disabled =
							!!selectedBuildingId &&
							rowBuilding !== selectedBuildingId;
						return (
							<Tooltip
								title={
									disabled
										? 'Cannot select lengths from different buildings'
										: undefined
								}
							>
								<Checkbox
									checked={selectedRowKeys.includes(record.id)}
									disabled={disabled}
									onChange={(e) => {
										const nextKeys = e.target.checked
											? [...selectedRowKeys, record.id]
											: selectedRowKeys.filter(
													(k) => k !== record.id
												);
										onSelectionChange(nextKeys, []);
									}}
								/>
							</Tooltip>
						);
					},
				},
			]
			: []),
	];

	const visibleColumns = columns.filter((col: any) => col.visible !== false);
	const tableScrollX = visibleColumns.reduce(
		(total: number, col: any) => total + (Number(col.width) || 140),
		0
	);

	return (
		<>
		<CustomResponsiveTable
			scroll={{
				x: tableScrollX,
				y: scrollY || 'calc(100vh - 310px)',
			}}
			columns={visibleColumns}
			data={data || []}
			loading={loading}
			rowKey="id"
			onRow={(record) => ({
				'data-testid': 'fastlane-material-length-row',
				'data-material-length-id': record.id,
				'data-material-length-location': getLocationDiscriminator(record),
				'data-inventory-location-id': getInventoryLocationId(record),
			} as HTMLAttributes<HTMLElement>)}
			excludeWidthColumn={true}
			onChange={handleTableChange}
			emptyTable="No material lengths found"
			size={isTablet ? 'small' : 'middle'}
			pagination={
				pagination
					? {
						...pagination,
						hideOnSinglePage: true,
						position: ['bottomCenter' as const],
						responsive: true,
						showLessItems: true,
						showSizeChanger: false,
						simple: isTablet,
						size: 'small',
						style: { margin: isTablet ? '2px 0 0' : '10px 0 0' },
					}
					: false
			}
		/>
		<MaterialHistoryDrawer
			materialLengthId={historyRecord?.id ?? null}
			friendlyId={historyRecord?.friendly_id}
			open={Boolean(historyRecord)}
			onClose={() => setHistoryRecord(null)}
		/>
		</>
	);
};

export default MaterialLengthsTable;
